/**
 * Exchanges action-filter scan engine (DE-9312).
 *
 * Toolbar actions (Waiting, Action, Open, Closed, Cancelled) use a **broad OData
 * prefilter** on Raw* fields, then **client refinement** via `applyActionFilterToRows`.
 * The API cannot return "page 3 of Waiting rows" directly — a sparse prefilter
 * means most OData rows are discarded after mapping and flag evaluation.
 *
 * This module:
 * 1. Fetches `PBHOrdersSQLOnly` in fixed-size OData chunks ($top=50).
 * 2. Keeps the **newest workflow row per ExchangeRequestId** (`snapshots`).
 * 3. Rebuilds the **matched** list after each chunk (client-filtered rows).
 * 4. Slices `matched` for the requested UI page (skip + rows).
 * 5. Caches scan progress in memory and `sessionStorage` (see persistence util).
 *
 * Primary consumer: `useExchangesPage` → `extendSqlOnlyActionScanForPage`.
 * RTK Query route: `fetchSqlOnlyActionFilteredPage` in `pbhApi.ts`.
 */
import { resolveExchangeRequestId } from "../views/Exchanges/utils/filterUtils";
import { resolveActionFilterCompanyId } from "../views/Exchanges/utils/sqlOnlyActionFilterUtils";
import {
  clearAllPersistedSqlOnlyActionScans,
  clearPersistedSqlOnlyActionScan,
  hydrateSqlOnlyActionScanState,
  persistSqlOnlyActionScanState,
} from "../views/Exchanges/utils/sqlOnlyActionScanPersistence";
import type { ExchangeSnapshot } from "../views/Exchanges/utils/sqlOnlyActionSnapshotUtils";
import {
  mergeLatestExchangeSnapshots,
  snapshotsToSortedRows,
} from "../views/Exchanges/utils/sqlOnlyActionSnapshotUtils";
import { apiService } from "./";
import {
  buildPbhOrdersSqlOnlyQueryOptions,
  type GetPbhOrdersArgs,
  type GetPbhOrdersResponse,
  type PbhOrdersSqlOnlyOrderQueryFn,
} from "./pbhOrdersSqlOnlyTypes";
import { SQL_ONLY_ODATA_MAX_TOP } from "./sqlOnlyODataConstants";

export { SQL_ONLY_ODATA_MAX_TOP };

/** Chunk size for SQL-only action export (rows: 0). */
export const SQL_ONLY_EXPORT_CHUNK_SIZE = SQL_ONLY_ODATA_MAX_TOP;

/**
 * Max rows returned for CSV export — matches PBHOrdersSQLOnly backend export limit
 * and `DOWNLOAD_ROW_LIMIT` toast in `useExchangesPage`.
 */
export const SQL_ONLY_MAX_EXPORT_ROWS = 100;

/** OData page size when scanning for client-refined action filter rows. */
export const SQL_ONLY_ACTION_SCAN_CHUNK_SIZE = SQL_ONLY_ODATA_MAX_TOP;

const parsePositiveInt = (
  value: string | undefined,
  fallback: number,
): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/** Max OData rows to scan per action-filtered list page (export uses separate cap). */
export const SQL_ONLY_ACTION_MAX_ODATA_SCAN = parsePositiveInt(
  process.env.REACT_APP_SQL_ONLY_ACTION_MAX_ODATA_SCAN,
  2000,
);

/** Max HTTP chunk requests per action-filtered list page load. */
export const SQL_ONLY_ACTION_MAX_CHUNK_REQUESTS = parsePositiveInt(
  process.env.REACT_APP_SQL_ONLY_ACTION_MAX_CHUNK_REQUESTS,
  40,
);

/** In-memory scan progress for one action-filter key (see `buildSqlOnlyActionScanKey`). */
interface SqlOnlyActionScanState {
  /** Newest mapped row per ExchangeRequestId across all chunks fetched so far. */
  snapshots: Map<string, ExchangeSnapshot>;
  /** Client-refined rows (Waiting/Action/etc.) derived from `snapshots`. */
  matched: unknown[];
  /** Next OData `$skip` — advances by **raw row count**, not matched count. */
  odataSkip: number;
  /** True when the last chunk returned fewer rows than the chunk size. */
  odataExhausted: boolean;
  /** Total raw OData rows consumed (for cap checks). */
  odataRowsScanned: number;
  /** HTTP chunk requests made for this scan key. */
  chunkRequests: number;
  /** True when scan caps were hit before OData was exhausted. */
  scanTruncated: boolean;
  /** Chunks fetched since this tab session started (not from hydration). */
  chunksFetchedThisSession: number;
}

/** Session-scoped scan cache keyed by filter identity (not pagination). */
const sqlOnlyActionScanCache = new Map<string, SqlOnlyActionScanState>();
/** Serialises concurrent deep-link finders for the same cache key. */
const sqlOnlyActionScanPromises = new Map<string, Promise<void>>();
/** Ensures only one chunk fetch runs per cache key at a time. */
const sqlOnlyActionScanChunkLocks = new Map<string, Promise<void>>();
/** Debounced timers for writing scan state to sessionStorage. */
const sqlOnlyActionScanPersistTimers = new Map<
  string,
  ReturnType<typeof setTimeout>
>();
/** One AbortController per cache key — aborted when filters change. */
const sqlOnlyActionScanAbortControllers = new Map<
  string,
  AbortController
>();
/**
 * Bumped by `invalidateSqlOnlyActionScanCache`. In-flight scan loops capture
 * the generation at start and stop if it changes (stale work after filter switch).
 */
let sqlOnlyActionScanGeneration = 0;

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const name = (error as { name?: string }).name;
  if (name === "AbortError" || name === "CanceledError") {
    return true;
  }
  const code = (error as { code?: string }).code;
  return code === "ERR_CANCELED";
};

const abortSqlOnlyActionScanRequests = (): void => {
  sqlOnlyActionScanAbortControllers.forEach((controller) => {
    controller.abort();
  });
  sqlOnlyActionScanAbortControllers.clear();
};

const getSqlOnlyActionScanAbortSignal = (key: string): AbortSignal => {
  const existing = sqlOnlyActionScanAbortControllers.get(key);
  if (existing && !existing.signal.aborted) {
    return existing.signal;
  }
  const controller = new AbortController();
  sqlOnlyActionScanAbortControllers.set(key, controller);
  return controller.signal;
};

/** Cache key: action + company + text/status filters + sort. Excludes skip/rows. */
const buildSqlOnlyActionScanKey = (args: GetPbhOrdersArgs): string =>
  JSON.stringify({
    action: args.action,
    companyId: resolveActionFilterCompanyId(args.user),
    property: args.property,
    query: args.query,
    inboundStatus: args.inboundStatus,
    outboundStatus: args.outboundStatus,
    sort: args.sort,
  });

/** Args for scan-cache lookup (pagination fields excluded from the key). */
export const toSqlOnlyActionScanArgs = (
  args: GetPbhOrdersArgs,
): GetPbhOrdersArgs => ({
  ...args,
  skip: 0,
  rows: 1,
  includeCount: false,
});

const resolveSqlOnlyActionScanKey = (args: GetPbhOrdersArgs): string =>
  buildSqlOnlyActionScanKey(toSqlOnlyActionScanArgs(args));

/** Exported for tests — stable action-filter scan cache key (no pagination). */
export const getSqlOnlyActionScanCacheKey = resolveSqlOnlyActionScanKey;

const getSqlOnlyOrderQuery = (): PbhOrdersSqlOnlyOrderQueryFn =>
  apiService.orderQueryODataSqlOnly.bind(
    apiService,
  ) as PbhOrdersSqlOnlyOrderQueryFn;

/** Recompute `matched` from all snapshots after a new chunk is merged. */
const rebuildSqlOnlyActionMatches = (
  state: SqlOnlyActionScanState,
  filterArgs: GetPbhOrdersArgs,
): void => {
  if (!filterArgs.action) {
    state.matched = snapshotsToSortedRows(state.snapshots);
    return;
  }

  const action = {
    name: filterArgs.action,
    ...(filterArgs.user ? { user: filterArgs.user } : {}),
  };
  const snapshotRows = snapshotsToSortedRows(state.snapshots);
  const result = apiService.applyActionFilterToRows(snapshotRows, action);
  state.matched = Array.isArray(result?.data) ? result.data : [];
};

/**
 * Return existing in-memory state, hydrate from sessionStorage, or create empty.
 * `freshScan` is handled by `resetSqlOnlyActionScanForFreshStart` before the scan
 * loop — do not pass it here during chunk fetches.
 */
const getSqlOnlyActionScanState = (
  key: string,
  filterArgs?: GetPbhOrdersArgs,
): SqlOnlyActionScanState => {
  const existing = sqlOnlyActionScanCache.get(key);
  if (existing) {
    return existing;
  }

  const hydrated = hydrateSqlOnlyActionScanState(key);
  const initial: SqlOnlyActionScanState = hydrated
    ? {
        snapshots: hydrated.snapshots,
        matched: [],
        odataSkip: hydrated.odataSkip,
        odataExhausted: hydrated.odataExhausted,
        odataRowsScanned: hydrated.odataRowsScanned,
        chunkRequests: hydrated.chunkRequests,
        scanTruncated: hydrated.scanTruncated,
        chunksFetchedThisSession: 0,
      }
    : {
        snapshots: new Map<string, ExchangeSnapshot>(),
        matched: [],
        odataSkip: 0,
        odataExhausted: false,
        odataRowsScanned: 0,
        chunkRequests: 0,
        scanTruncated: false,
        chunksFetchedThisSession: 0,
      };

  if (filterArgs?.action) {
    rebuildSqlOnlyActionMatches(initial, filterArgs);
  } else if (hydrated) {
    initial.matched = snapshotsToSortedRows(initial.snapshots);
  }

  sqlOnlyActionScanCache.set(key, initial);
  return initial;
};

/** Drop in-memory + persisted scan state so the next lookup starts from $skip=0. */
const resetSqlOnlyActionScanForFreshStart = (key: string): void => {
  sqlOnlyActionScanCache.delete(key);
  clearPersistedSqlOnlyActionScan(key);
};

/** Debounce sessionStorage writes — scan loops may fetch many chunks quickly. */
const schedulePersistSqlOnlyActionScan = (
  key: string,
  state: SqlOnlyActionScanState,
): void => {
  const existingTimer = sqlOnlyActionScanPersistTimers.get(key);
  if (existingTimer) {
    clearTimeout(existingTimer);
  }

  const timer = setTimeout(() => {
    sqlOnlyActionScanPersistTimers.delete(key);
    persistSqlOnlyActionScanState(key, state);
  }, 300);

  sqlOnlyActionScanPersistTimers.set(key, timer);
};

const findMatchedExchangeIndex = (
  state: SqlOnlyActionScanState,
  exchangeRequestId: string,
): number => {
  const idStr = String(exchangeRequestId);
  return state.matched.findIndex((row) => {
    const erId = resolveExchangeRequestId(row);
    return erId != null && String(erId) === idStr;
  });
};

/**
 * True when we can slice the requested UI page from `matched` without more OData.
 * Requires `uiSkip + uiPageSize + 1` matches when more OData may exist — the
 * extra row detects "has next page" (overfetch-by-one, same as normal list).
 */
const canSliceSqlOnlyActionPage = (
  state: SqlOnlyActionScanState,
  uiSkip: number,
  uiPageSize: number,
): boolean => {
  const targetLength = uiSkip + uiPageSize + 1;
  if (state.matched.length >= targetLength) {
    return true;
  }
  return state.odataExhausted && state.matched.length > uiSkip;
};

/** Slice `matched` for one UI page; clamps skip when the list shrank (filter switch). */
const sliceSqlOnlyActionPage = (
  state: SqlOnlyActionScanState,
  args: GetPbhOrdersArgs,
): GetPbhOrdersResponse => {
  const pageSize = args.rows || 30;
  const targetSkip = args.skip || 0;
  const effectiveSkip = Math.min(
    targetSkip,
    Math.max(0, state.matched.length - pageSize),
  );
  const effectiveEnd = effectiveSkip + pageSize + 1;

  return {
    data: state.matched.slice(effectiveSkip, effectiveEnd),
    count: state.matched.length,
    scanComplete: state.odataExhausted,
  };
};

/**
 * Fetch one OData chunk, merge into snapshots, rebuild matches, advance skip.
 * Uses `skipActionClientFilter: true` — refinement happens here, not in api.service.
 */
const appendSqlOnlyActionScanChunk = async (
  filterArgs: GetPbhOrdersArgs,
  state: SqlOnlyActionScanState,
  orderQuery: PbhOrdersSqlOnlyOrderQueryFn,
  startGeneration: number,
): Promise<void> => {
  if (
    state.odataExhausted ||
    state.scanTruncated ||
    sqlOnlyActionScanGeneration !== startGeneration
  ) {
    return;
  }

  const scanKey = resolveSqlOnlyActionScanKey(filterArgs);
  const signal = getSqlOnlyActionScanAbortSignal(scanKey);

  let result: Awaited<ReturnType<PbhOrdersSqlOnlyOrderQueryFn>>;
  try {
    result = await orderQuery(
      buildPbhOrdersSqlOnlyQueryOptions({
        ...toSqlOnlyActionScanArgs(filterArgs),
        rows: SQL_ONLY_ACTION_SCAN_CHUNK_SIZE,
        skip: state.odataSkip,
        includeCount: false,
        skipActionClientFilter: true,
        signal,
      }),
      filterArgs.sort,
      false,
    );
  } catch (error) {
    if (signal.aborted || isAbortError(error)) {
      return;
    }
    throw error;
  }

  if (sqlOnlyActionScanGeneration !== startGeneration || signal.aborted) {
    return;
  }

  const chunk = Array.isArray(result?.data) ? result.data : [];
  const rawCount =
    typeof result?.odataRowCount === "number"
      ? result.odataRowCount
      : chunk.length;

  mergeLatestExchangeSnapshots(state.snapshots, chunk);
  rebuildSqlOnlyActionMatches(state, filterArgs);
  // Advance by raw OData rows — sparse prefilters may yield few matches per chunk.
  state.odataSkip += rawCount;
  state.odataRowsScanned += rawCount;
  state.chunkRequests += 1;
  state.chunksFetchedThisSession += 1;

  if (rawCount < SQL_ONLY_ACTION_SCAN_CHUNK_SIZE) {
    state.odataExhausted = true;
  }

  if (
    !state.odataExhausted &&
    (state.odataRowsScanned >= SQL_ONLY_ACTION_MAX_ODATA_SCAN ||
      state.chunkRequests >= SQL_ONLY_ACTION_MAX_CHUNK_REQUESTS)
  ) {
    state.scanTruncated = true;
  }

  schedulePersistSqlOnlyActionScan(
    resolveSqlOnlyActionScanKey(filterArgs),
    state,
  );
};

/** Queue chunk fetches for the same cache key — prevents duplicate OData requests. */
const withSqlOnlyActionScanChunkLock = async (
  key: string,
  fn: () => Promise<void>,
): Promise<void> => {
  const previous =
    sqlOnlyActionScanChunkLocks.get(key) ?? Promise.resolve();
  let release!: () => void;
  const current = new Promise<void>((resolve) => {
    release = resolve;
  });
  sqlOnlyActionScanChunkLocks.set(
    key,
    previous.then(() => current),
  );
  await previous;
  try {
    await fn();
  } finally {
    release();
  }
};

const shouldStopSqlOnlyActionScan = (
  state: SqlOnlyActionScanState,
  startGeneration: number,
): boolean =>
  state.odataExhausted ||
  state.scanTruncated ||
  state.odataRowsScanned >= SQL_ONLY_ACTION_MAX_ODATA_SCAN ||
  state.chunkRequests >= SQL_ONLY_ACTION_MAX_CHUNK_REQUESTS ||
  sqlOnlyActionScanGeneration !== startGeneration;

/**
 * Scan OData chunks until `matched` can fill the requested UI page, or caps /
 * exhaustion stop further fetches.
 */
async function ensureSqlOnlyActionScanForPage(
  filterArgs: GetPbhOrdersArgs,
  uiSkip: number,
  uiPageSize: number,
  orderQuery: PbhOrdersSqlOnlyOrderQueryFn,
  options?: { freshScan?: boolean },
): Promise<SqlOnlyActionScanState> {
  const key = resolveSqlOnlyActionScanKey(filterArgs);
  if (options?.freshScan === true) {
    resetSqlOnlyActionScanForFreshStart(key);
  }
  const targetLength = uiSkip + uiPageSize + 1;
  const startGeneration = sqlOnlyActionScanGeneration;
  let scanning = true;

  while (scanning) {
    const state = getSqlOnlyActionScanState(key, filterArgs);

    if (
      canSliceSqlOnlyActionPage(state, uiSkip, uiPageSize) ||
      shouldStopSqlOnlyActionScan(state, startGeneration)
    ) {
      scanning = false;
      continue;
    }

    await withSqlOnlyActionScanChunkLock(key, async () => {
      const lockedState = getSqlOnlyActionScanState(key, filterArgs);
      if (
        canSliceSqlOnlyActionPage(lockedState, uiSkip, uiPageSize) ||
        shouldStopSqlOnlyActionScan(lockedState, startGeneration)
      ) {
        return;
      }
      await appendSqlOnlyActionScanChunk(
        filterArgs,
        lockedState,
        orderQuery,
        startGeneration,
      );
    });
  }

  const finalState = getSqlOnlyActionScanState(key, filterArgs);
  if (
    sqlOnlyActionScanGeneration === startGeneration &&
    finalState.matched.length < targetLength &&
    !finalState.odataExhausted &&
    (finalState.odataRowsScanned >= SQL_ONLY_ACTION_MAX_ODATA_SCAN ||
      finalState.chunkRequests >= SQL_ONLY_ACTION_MAX_CHUNK_REQUESTS)
  ) {
    finalState.scanTruncated = true;
  }

  return finalState;
}

/**
 * Drop cached action-filter scans. Call when toolbar filters, sort, or search
 * change — aborts in-flight HTTP, clears memory + sessionStorage, bumps generation.
 */
export const invalidateSqlOnlyActionScanCache = (): void => {
  abortSqlOnlyActionScanRequests();
  sqlOnlyActionScanPersistTimers.forEach((timer) => {
    clearTimeout(timer);
  });
  sqlOnlyActionScanPersistTimers.clear();
  sqlOnlyActionScanCache.clear();
  sqlOnlyActionScanPromises.clear();
  sqlOnlyActionScanChunkLocks.clear();
  clearAllPersistedSqlOnlyActionScans();
  sqlOnlyActionScanGeneration += 1;
};

/** UI page result including scan status flags for warning banners. */
export interface SqlOnlyActionPageSlice {
  data: unknown[];
  count: number;
  /** All OData rows for this filter have been consumed. */
  scanComplete: boolean;
  /** Scan stopped early due to row/chunk caps. */
  scanTruncated: boolean;
  /**
   * Show "scan unavailable" warning — truncated and either we fetched this
   * session or the cached slice cannot fill the requested page.
   */
  scanUnavailable: boolean;
}

const buildSqlOnlyActionPageSlice = (
  state: SqlOnlyActionScanState,
  filterArgs: GetPbhOrdersArgs,
  uiSkip: number,
  uiPageSize: number,
): SqlOnlyActionPageSlice => {
  const pageResponse = sliceSqlOnlyActionPage(state, {
    ...filterArgs,
    skip: uiSkip,
    rows: uiPageSize,
  });
  const canFillPage = canSliceSqlOnlyActionPage(state, uiSkip, uiPageSize);
  const hadFetchesThisSession = state.chunksFetchedThisSession > 0;

  return {
    data: pageResponse.data ?? [],
    count: state.matched.length,
    scanComplete: state.odataExhausted,
    scanTruncated: state.scanTruncated,
    scanUnavailable:
      state.scanTruncated && (hadFetchesThisSession || !canFillPage),
  };
};

/**
 * Return a cached page slice without fetching OData, or null if the cache
 * cannot yet fill the requested page.
 */
export const sliceSqlOnlyActionFromCache = (
  filterArgs: GetPbhOrdersArgs,
  uiSkip: number,
  uiPageSize: number,
): SqlOnlyActionPageSlice | null => {
  const key = resolveSqlOnlyActionScanKey(filterArgs);
  const state = sqlOnlyActionScanCache.get(key);
  if (!state || !canSliceSqlOnlyActionPage(state, uiSkip, uiPageSize)) {
    return null;
  }

  return buildSqlOnlyActionPageSlice(
    state,
    filterArgs,
    uiSkip,
    uiPageSize,
  );
};

export const sliceSqlOnlyActionPageFromCache = sliceSqlOnlyActionFromCache;

/** Result of scanning for a deep-linked `exchangeRequestId` in the action list. */
export interface SqlOnlyActionDeepLinkMatch {
  /** Page skip to show the exchange (0 if not found). */
  skip: number;
  found: boolean;
  scanTruncated: boolean;
  /** Index in the `matched` array (-1 if not found). */
  index: number;
}

/**
 * Scan until `exchangeRequestId` appears in `matched`, then compute its page skip.
 * Used when the URL has `exchangeRequestId` + `action` but no `skip`.
 */
export const findSqlOnlyActionMatchPageSkip = async (
  filterArgs: GetPbhOrdersArgs,
  exchangeRequestId: string,
  pageSize: number,
): Promise<SqlOnlyActionDeepLinkMatch> => {
  const key = resolveSqlOnlyActionScanKey(filterArgs);
  const idStr = String(exchangeRequestId);
  const orderQuery = getSqlOnlyOrderQuery();
  const startGeneration = sqlOnlyActionScanGeneration;

  const runFind = async (): Promise<void> => {
    const state = getSqlOnlyActionScanState(key, filterArgs);

    while (
      findMatchedExchangeIndex(state, idStr) === -1 &&
      !state.odataExhausted &&
      !state.scanTruncated &&
      state.odataRowsScanned < SQL_ONLY_ACTION_MAX_ODATA_SCAN &&
      state.chunkRequests < SQL_ONLY_ACTION_MAX_CHUNK_REQUESTS &&
      sqlOnlyActionScanGeneration === startGeneration
    ) {
      await appendSqlOnlyActionScanChunk(
        filterArgs,
        state,
        orderQuery,
        startGeneration,
      );
    }

    if (
      sqlOnlyActionScanGeneration === startGeneration &&
      findMatchedExchangeIndex(state, idStr) === -1 &&
      !state.odataExhausted &&
      (state.odataRowsScanned >= SQL_ONLY_ACTION_MAX_ODATA_SCAN ||
        state.chunkRequests >= SQL_ONLY_ACTION_MAX_CHUNK_REQUESTS)
    ) {
      state.scanTruncated = true;
    }
  };

  const existing = sqlOnlyActionScanPromises.get(key);
  if (existing) {
    await existing;
  }

  const findPromise = runFind().finally(() => {
    if (sqlOnlyActionScanPromises.get(key) === findPromise) {
      sqlOnlyActionScanPromises.delete(key);
    }
  });
  sqlOnlyActionScanPromises.set(key, findPromise);
  await findPromise;

  const state = getSqlOnlyActionScanState(key, filterArgs);
  const index = findMatchedExchangeIndex(state, idStr);
  const skip = index >= 0 ? Math.floor(index / pageSize) * pageSize : 0;

  return {
    skip,
    found: index >= 0,
    scanTruncated: state.scanTruncated,
    index,
  };
};

/**
 * Main entry for Exchanges UI pagination. Returns cached slice when possible,
 * otherwise scans OData until the page can be filled (or caps hit).
 */
export const extendSqlOnlyActionScanForPage = async (
  filterArgs: GetPbhOrdersArgs,
  uiSkip: number,
  uiPageSize: number,
  options?: { freshScan?: boolean },
): Promise<SqlOnlyActionPageSlice> => {
  const cached = sliceSqlOnlyActionFromCache(
    filterArgs,
    uiSkip,
    uiPageSize,
  );
  if (cached && !options?.freshScan) {
    return cached;
  }

  const orderQuery = getSqlOnlyOrderQuery();
  const state = await ensureSqlOnlyActionScanForPage(
    filterArgs,
    uiSkip,
    uiPageSize,
    orderQuery,
    options,
  );

  return buildSqlOnlyActionPageSlice(
    state,
    filterArgs,
    uiSkip,
    uiPageSize,
  );
};

/**
 * One-shot chunked OData scan for CSV export (`rows === 0`).
 * No session cache — dedupes snapshots, client-filters, caps at 100 rows.
 */
export async function fetchSqlOnlyChunkedExport(
  args: GetPbhOrdersArgs,
  orderQuery: PbhOrdersSqlOnlyOrderQueryFn,
): Promise<GetPbhOrdersResponse> {
  const snapshots = new Map<string, ExchangeSnapshot>();
  let offset = 0;

  while (snapshots.size < SQL_ONLY_MAX_EXPORT_ROWS) {
    const orderQueryOptions = buildPbhOrdersSqlOnlyQueryOptions({
      ...args,
      rows: SQL_ONLY_EXPORT_CHUNK_SIZE,
      skip: offset,
      includeCount: false,
      skipActionClientFilter: !!args.action,
    });

    const result = await orderQuery(orderQueryOptions, args.sort, false);
    const chunk = Array.isArray(result?.data) ? result.data : [];

    if (chunk.length === 0) {
      break;
    }

    mergeLatestExchangeSnapshots(snapshots, chunk);
    offset += SQL_ONLY_EXPORT_CHUNK_SIZE;

    const odataRowCount =
      typeof result?.odataRowCount === "number"
        ? result.odataRowCount
        : chunk.length;
    if (odataRowCount < SQL_ONLY_EXPORT_CHUNK_SIZE) {
      break;
    }
  }

  const snapshotRows = snapshotsToSortedRows(snapshots);
  let data = snapshotRows;
  if (args.action) {
    const action = {
      name: args.action,
      ...(args.user ? { user: args.user } : {}),
    };
    const filtered = apiService.applyActionFilterToRows(
      snapshotRows,
      action,
    );
    data = Array.isArray(filtered?.data) ? filtered.data : [];
  }
  data = data.slice(0, SQL_ONLY_MAX_EXPORT_ROWS);

  return {
    data,
    count: data.length,
  };
}

/**
 * RTK Query route for action-filter list fetch. Same scan engine as
 * `extendSqlOnlyActionScanForPage` but returns a plain `GetPbhOrdersResponse`.
 */
export async function fetchSqlOnlyActionFilteredPage(
  args: GetPbhOrdersArgs,
  orderQuery: PbhOrdersSqlOnlyOrderQueryFn,
): Promise<GetPbhOrdersResponse> {
  const pageSize = args.rows || 30;
  const uiSkip = args.skip || 0;
  const state = await ensureSqlOnlyActionScanForPage(
    args,
    uiSkip,
    pageSize,
    orderQuery,
  );
  const slice = sliceSqlOnlyActionPage(state, {
    ...args,
    rows: pageSize,
  });
  return {
    data: slice.data,
    count: slice.count,
    scanComplete: slice.scanComplete,
  };
}
